import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions,
  Button,
  Stack,
  TextField,
  IconButton,
  Tooltip,
  Badge,
  Container,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Box,
  useTheme,
  useMediaQuery,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { RadioButtonChecked, RadioButtonUnchecked } from '@mui/icons-material';
import { fetchClasses, recordClass, removeRecord, setClassProgress } from '../../features/techniques/techniquesSlice';
import { fetchNotes } from '../../features/notes/notesSlice';
import NotesIcon from '@mui/icons-material/Notes';
import NotesDialog from './NotesDialog';
import { fetchSchedules } from '../../features/schedules/schedulesSlice';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { createGoogleCalendarUrl } from '../../utils/calendarUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import NoteIcon from '@mui/icons-material/Note';
import { Check as CheckIcon, Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';

// Move these helper functions to the top, before any components
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  // 确保日期字符串是 YYYY-MM-DD 格式
  const [year, month, day] = dateString.split('-');
  // 创建本地时区的日期对象
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  // 使用本地时区格式化
  return date.toLocaleDateString();
};

const formatDateForInput = (date) => {
  // 确保使用本地时区的年月日
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const StyledTable = styled(Table)`
  table-layout: fixed;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

const StyledTableCell = styled(TableCell)`
  padding: 12px 16px;
  border-right: 1px solid rgba(224, 224, 224, 0.4);
  
  &:last-child {
    border-right: none;
  }
`;

const ProgressButtonsContainer = styled('div')`
  display: flex;
  gap: 8px;
`;

const ProgressButton = styled(({ filled, date, onClick, disabled, loading, isExtra, ...props }) => (
  <Tooltip title={date ? formatDateForDisplay(date) : ''}>
    <span>
      <IconButton
        onClick={onClick}
        disabled={disabled || loading}
        size="small"
        {...props}
      >
        {loading ? (
          <CircularProgress size={20} />
        ) : (
          filled ? 
            <RadioButtonChecked color={isExtra ? "error" : "primary"} /> : 
            <RadioButtonUnchecked />
        )}
      </IconButton>
    </span>
  </Tooltip>
))`
  padding: 4px;
`;

const isEveningOrSaturdayClass = (schedule) => {
  if (!schedule) return false;
  
  // Get the day of week (0 = Sunday, 6 = Saturday)
  const date = new Date(schedule.year, schedule.month - 1, schedule.day);
  const dayOfWeek = date.getDay();
  
  // Get the hour from the time string
  const hour = parseInt(schedule.time.split(':')[0]);
  
  // Return true for Saturday classes or evening classes (after 1pm)
  return dayOfWeek === 6 || hour >= 13;
};

const TechniqueTracker = () => {
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const { classes, userProgress, loading, loadingActions, error } = useSelector((state) => state.techniques);
  const { items: notes } = useSelector((state) => state.notes);
  const { schedules } = useSelector((state) => state.schedules);
  const currentUser = useSelector((state) => state.auth.user);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [filters, setFilters] = useState({
    class_number: '',
    position: '',
    technique_type: '',
    search: ''
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const classRefs = useRef({});
  const techniqueRefs = useRef({});
  const [editingDate, setEditingDate] = useState(null);
  const [tempDate, setTempDate] = useState('');

  useEffect(() => {
    if (token) {
      dispatch(fetchClasses());
      dispatch(fetchSchedules());
    }
  }, [dispatch, token]);

  useEffect(() => {
    // 从 URL 获取 class 参数
    const urlParams = new URLSearchParams(window.location.search);
    const classParam = urlParams.get('class');
    const shouldScroll = urlParams.get('scroll') === 'true';

    if (classParam) {
      setSelectedClass(classParam); // 设置选中的课程

      if (shouldScroll) {
        // 立即滚动到目标元素
        const element = classRefs.current[`class-${classParam}`];
        if (element) {
          // 使用 scrollIntoView 但禁用平滑滚动
          element.scrollIntoView({
            block: 'center',
            behavior: 'auto'
          });

          // 10秒后清除高亮
          setTimeout(() => {
            setSelectedClass(null);
          }, 10000); // 改为 10 秒
        }
      }
    }
  }, [classes]);

  useEffect(() => {
    // 从 URL 参数中获取 class number
    const params = new URLSearchParams(location.search);
    const classNumber = params.get('class');
    const shouldScroll = params.get('scroll') === 'true';

    if (classNumber && shouldScroll && classRefs.current[classNumber]) {
      // 获取目标元素
      const element = classRefs.current[classNumber];
      
      // 计算元素应该滚动到的位置（使其在屏幕中央）
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);

      // 使用平滑滚动
      window.scrollTo({
        top: middle,
        behavior: 'smooth'
      });

      // 添加高亮动画
      element.style.animation = 'highlight 2s';
    }
  }, [location]);

  useEffect(() => {
    // 从 URL 获取 class 参数
    const urlParams = new URLSearchParams(window.location.search);
    const classParam = urlParams.get('class');
    const shouldScroll = urlParams.get('scroll') === 'true';

    if (classParam) {
      // 设置选中的课程
      setSelectedClass(classParam);
      
      // 设置筛选器为对应的课程号
      setFilters(prev => ({
        ...prev,
        class_number: classParam
      }));

      // 如果需要滚动，等待组件渲染后执行滚动
      if (shouldScroll) {
        setTimeout(() => {
          // 找到对应课程号的第一个技术
          const targetTechnique = classes.find(t => t.number === classParam);
          if (targetTechnique) {
            const element = techniqueRefs.current[`class-${classParam}`];
            if (element) {
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }
          }
        }, 100);
      }
    }
  }, [classes]);

  // 添加筛选逻辑
  const filteredTechniques = useMemo(() => {
    return classes.filter(technique => {
      if (filters.class_number && technique.number !== filters.class_number) {
        return false;
      }
      if (filters.position && technique.position !== filters.position) {
        return false;
      }
      if (filters.technique_type && technique.technique_type !== filters.technique_type) {
        return false;
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        return technique.name.toLowerCase().includes(searchLower) ||
               technique.description.toLowerCase().includes(searchLower);
      }
      return true;
    });
  }, [classes, filters.class_number, filters.position, filters.technique_type, filters.search]);

  if (!token) {
    return (
      <Container>
        <Alert severity="warning">Please log in to view your technique tracker.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error">
          {error}
        </Alert>
      </Container>
    );
  }

  if (!classes || classes.length === 0) {
    return (
      <Container>
        <Alert severity="info">
          No classes available.
        </Alert>
      </Container>
    );
  }

  const handleProgressClick = async (classId, index, existingDate) => {
    try {
      if (existingDate) {
        // Show confirmation dialog for removing record
        setSelectedAction({
          classId,
          index,
          existingDate,
        });
        setOpenDialog(true);
      } else {
        // Direct add record without dialog
        const result = await dispatch(recordClass({ 
          classId, 
          index 
        })).unwrap();
        
        if (!result || result.error) {
          console.error('Failed to record class:', result?.error);
          return;
        }
      }
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedAction(null);
  };

  const handleRemoveRecord = async () => {
    if (selectedAction) {
      try {
        const classId = selectedAction.classId;
        const buttonIndex = selectedAction.index;
        
        // Close dialog immediately
        handleDialogClose();

        // Optimistically update UI
        const currentProgress = userProgress[classId];
        const newDates = [...(currentProgress?.dates || [])];
        
        // Ensure array has 5 elements
        while (newDates.length < 5) {
          newDates.push(null);
        }
        
        // Set current and all subsequent buttons to null
        for (let i = buttonIndex; i < 5; i++) {
          newDates[i] = null;
        }
        
        // Calculate new completed classes count
        const newCompletedClasses = newDates.filter(date => date !== null).length;
        
        dispatch(setClassProgress({
          classId,
          progress: {
            completed_classes: newCompletedClasses,
            dates: newDates
          }
        }));

        // Perform actual deletion
        await dispatch(removeRecord({ 
          classId, 
          buttonIndex 
        })).unwrap();

      } catch (error) {
        console.error('Failed to remove record:', error);
        // Could add error handling here to show a notification
      }
    }
  };

  const handleStartEdit = (originalDate) => {
    setEditingDate(originalDate);
    // originalDate 应该已经是 YYYY-MM-DD 格式，直接使用
    setTempDate(originalDate);
  };

  const handleCancelEdit = () => {
    setEditingDate(null);
    setTempDate('');
  };

  const handleDateChange = (event) => {
    setTempDate(event.target.value);
  };

  const handleConfirmEdit = async () => {
    try {
      if (selectedAction && tempDate) {
        const classId = selectedAction.classId;
        const index = selectedAction.index;

        // 直接使用 tempDate (已经是 YYYY-MM-DD 格式)
        const timestamp = tempDate;

        // Optimistically update UI
        const currentProgress = userProgress[classId];
        const newDates = [...(currentProgress?.dates || [])];
        
        while (newDates.length < 5) {
          newDates.push(null);
        }
        
        newDates[index] = timestamp; // 直接使用 YYYY-MM-DD 格式
        
        dispatch(setClassProgress({
          classId,
          progress: {
            completed_classes: newDates.filter(date => date !== null).length,
            dates: newDates
          }
        }));

        // Perform actual API call
        await dispatch(recordClass({ 
          classId,
          index,
          timestamp
        })).unwrap();
        
        handleDialogClose();
        setEditingDate(null);
        setTempDate('');
      }
    } catch (error) {
      console.error('Error updating date:', error);
    }
  };

  const handleDeleteRecord = () => {
    if (selectedAction) {
      dispatch(removeRecord({
        classId: selectedAction.classId,
        buttonIndex: selectedAction.index
      }));
      handleDialogClose();
    }
  };

  const isButtonDisabled = (dates, index) => {
    if (index === 0) return false;
    return !dates[index - 1];
  };

  const handleNotesClick = (classId) => {
    setSelectedClass(classId);
    setNotesDialogOpen(true);
  };

  const handleNoteClick = (classId) => {
    navigate(`/techniques/${classId}/notes`);
  };

  const getClassColor = (time, date) => {
    // Get the day of week (0 = Sunday, 6 = Saturday)
    const dayOfWeek = date.getDay();
    
    // If it's Saturday, use blue color with 70% opacity
    if (dayOfWeek === 6) {
      return 'rgba(25, 118, 210, 0.9)';  // #1976d2 with 70% opacity
    }
    
    // Otherwise, use existing color logic with 70% opacity
    const hour = parseInt(time.split(':')[0]);
    return hour < 13 
      ? 'rgba(76, 175, 80, 0.9)'   // #4caf50 (green) with 70% opacity
      : 'rgba(245, 124, 0, 0.9)';  // #f57c00 (orange) with 70% opacity
  };

  const formatNextClass = (nextClass) => {
    if (!nextClass) return null;
    
    return nextClass.split(' && ').map((classInfo, index) => {
      const [timeOfDay, details] = classInfo.split('||');
      return (
        <Box
          key={index} 
          sx={{ 
            color: 'white',
            bgcolor: getClassColor(timeOfDay, new Date(nextClass)),
            p: 1,
            borderRadius: 1,
            mb: 0.5,
            width: '100%'
          }}
        >
          <Typography variant="body2">
            {details}
          </Typography>
        </Box>
      );
    });
  };

  const getNextClass = (classNumber) => {
    if (!schedules || schedules.length === 0) return null;

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const currentDay = today.getDate();
    const currentHour = today.getHours();
    const currentMinutes = today.getMinutes();

    // Filter schedules to only include evening and Saturday classes
    const filteredSchedules = schedules.filter(schedule => {
      // Convert both to strings for comparison
      const scheduleClassNum = String(schedule.class_number);
      const targetClassNum = String(classNumber);
      return scheduleClassNum === targetClassNum && isEveningOrSaturdayClass(schedule);
    });

    // Find the next class from filtered schedules
    const nextClass = filteredSchedules.find(schedule => {
      const scheduleYear = parseInt(schedule.year);
      const scheduleMonth = parseInt(schedule.month);
      const scheduleDay = parseInt(schedule.day);
      const [scheduleHour, scheduleMinutes] = schedule.time.split(':').map(Number);

      // Create Date objects for comparison
      const scheduleDate = new Date(scheduleYear, scheduleMonth - 1, scheduleDay, scheduleHour, scheduleMinutes);
      const isAfterNow = scheduleDate > today;

      return isAfterNow;
    });

    return nextClass;
  };

  const formatNextClassDisplay = (schedule) => {
    if (!schedule) return null;

    const date = new Date(schedule.year, schedule.month - 1, schedule.day);
    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
    const dateStr = `${dayOfWeek}, ${schedule.month_name} ${schedule.day}`;
    const diffDays = Math.ceil(
      (new Date(schedule.year, schedule.month - 1, schedule.day) - new Date()) / (1000 * 60 * 60 * 24)
    );

    // Calculate end time (start time + 1 hour)
    const [hours, minutes] = schedule.time.split(':').map(Number);
    const endTime = `${(hours + 1).toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    const timeRange = `${schedule.time} - ${endTime}`;

    // 使用相同的颜色函数
    const bgColor = getClassColor(schedule.time, new Date(schedule.year, schedule.month - 1, schedule.day));

    return (
      <Box sx={{ 
        p: 0.75,
        borderRadius: 1,
        bgcolor: bgColor,
        color: 'white',
        minWidth: '180px',
        cursor: 'pointer',
        '&:hover': {
          opacity: 0.9
        }
      }}
      onClick={() => {
        navigate(`/schedule?highlight=${schedule.year}-${schedule.month}-${schedule.day}`);
      }}
      >
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 0.5
        }}>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            {`${dateStr}, ${timeRange}`}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ 
          opacity: 0.9,
          display: 'block'
        }}>
          {diffDays === 0 ? 'today' : 
           diffDays === 1 ? 'tomorrow' : 
           `in ${diffDays} days`}
        </Typography>
      </Box>
    );
  };

  const NotesButton = ({ classItem, loading }) => (
    <Tooltip 
      title={
        loading ? "Loading..." : 
        classItem.notes_count === 0 ? "Add Notes" :
        classItem.notes_count === 1 ? "1 note" :
        `${classItem.notes_count} notes`      }
      arrow
      placement="top"
    >
      <Box>  {/* 使用 Box 包装以避免 Tooltip 的 disabled 警告 */}
        <IconButton 
          onClick={() => handleNoteClick(classItem.id)}
          disabled={loading}
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <Badge 
            badgeContent={classItem.notes_count} 
            color="primary"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.7rem',
                minWidth: '16px',
                height: '16px',
                padding: '0 4px'
              }
            }}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              <NotesIcon 
                sx={{ 
                  color: classItem.notes_count > 0 ? 'primary.main' : 'action.active'
                }} 
              />
            )}
          </Badge>
        </IconButton>
      </Box>
    </Tooltip>
  );

  // 定义动画关键帧
  const highlightAnimation = keyframes({
    '0%': {
      backgroundColor: alpha(theme.palette.primary.main, 0.3),
      transform: 'scale(1.01)',
    },
    '5%': {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
      transform: 'scale(1)',
    },
    '95%': {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
      transform: 'scale(1)',
    },
    '100%': {
      backgroundColor: theme.palette.action.hover,
      transform: 'scale(1)',
    },
  });

  // 修改卡片样式函数
  const getCardStyle = (classNumber) => ({
    p: 2,
    mb: 2,
    borderRadius: 1,
    ...(classNumber === selectedClass && {
      animation: `${highlightAnimation} 10s ease-in-out forwards`,
      position: 'relative',
      zIndex: 1,
    })
  });

  return (
    <>
      {isMobile ? (
        <Container sx={{ mt: 2, mb: 2 }}>
          {classes.map((classItem) => {
            const progress = userProgress[classItem.id] || { dates: [] };
            const nextClass = getNextClass(classItem.number);
            
            return (
              <Paper
                key={classItem.id}
                ref={el => classRefs.current[`class-${classItem.number}`] = el}
                elevation={3}
                sx={getCardStyle(classItem.number)}
              >
                {/* Header */}
                <Box>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 2
                  }}>
                    <Typography 
                      variant="h6"
                      sx={{ fontWeight: 600 }}
                    >
                      Class {classItem.number}
                    </Typography>
                    <NotesButton 
                      classItem={classItem}
                      loading={loadingActions[`notes_${classItem.id}`]}
                    />
                  </Box>

                  {/* Progress Buttons */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1,
                    mb: 2.5,
                    px: 0
                  }}>
                      {[0, 1, 2, 3, 4].map((index) => (
                        <ProgressButton
                          key={index}
                          filled={progress.dates && progress.dates[index]}
                          date={progress.dates && progress.dates[index]}
                          onClick={() => handleProgressClick(
                            classItem.id,
                            index,
                            progress.dates && progress.dates[index]
                          )}
                          disabled={isButtonDisabled(progress.dates || [], index)}
                          loading={loadingActions[classItem.id]}
                          isExtra={index >= 3}
                          sx={{ 
                            flex: 1,
                            '& .MuiIconButton-root': {
                              padding: '6px'
                            }
                          }}
                        />
                      ))}
                  </Box>
                  </Box>

                {/* Techniques */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 1,
                      fontWeight: 500
                    }}
                  >
                    Techniques
                  </Typography>
                    {classItem.techniques.map((technique) => (
                    <Box 
                      key={technique.id} 
                      ref={el => techniqueRefs.current[`class-${technique.class_number}`] = el}
                      sx={{ 
                        display: 'flex',
                        alignItems: 'flex-start',
                        mb: 0.75
                      }}
                    >
                      <Box 
                        component="span" 
                        sx={{ 
                          mr: 1,
                          mt: 0.7,
                          width: 3,
                          height: 3,
                          borderRadius: '50%',
                          bgcolor: 'text.primary',
                          flexShrink: 0
                        }} 
                      />
                      <Typography 
                        variant="body2"
                        sx={{ 
                          fontSize: '0.875rem',
                          lineHeight: 1.5
                        }}
                      >
                        {technique.name_with_details}
                      </Typography>
                    </Box>
                    ))}
                  </Box>

                {/* Next Class Card */}
                {nextClass && (
                  <Box 
                    sx={{ 
                      mx: -2.5,
                      mb: -2.5,
                      borderBottomLeftRadius: 'inherit',
                      borderBottomRightRadius: 'inherit',
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      onClick={() => {
                        navigate(`/schedule?highlight=${nextClass.year}-${nextClass.month}-${nextClass.day}`);
                      }}
                      sx={{
                        bgcolor: getClassColor(nextClass.time, new Date(nextClass.year, nextClass.month - 1, nextClass.day)),
                        color: 'white',
                        p: 1.25,
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.9
                        }
                      }}
                    >
                      <Typography 
                        variant="body2"
                        sx={{ 
                          fontWeight: 600,
                          mb: 0.25,
                          fontSize: '0.875rem'
                        }}
                      >
                        {(() => {
                          const date = new Date(nextClass.year, nextClass.month - 1, nextClass.day);
                          const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
                          const [hours, minutes] = nextClass.time.split(':').map(Number);
                          const endTime = `${(hours + 1).toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                          return `${dayOfWeek}, ${nextClass.month_name} ${nextClass.day}, ${nextClass.time} - ${endTime}`;
                        })()}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          opacity: 0.85,
                          fontSize: '0.75rem',
                          display: 'block'
                        }}
                      >
                        {(() => {
                          const date = new Date(nextClass.year, nextClass.month - 1, nextClass.day);
                          const diffDays = Math.ceil((date - new Date()) / (1000 * 60 * 60 * 24));
                          return diffDays === 0 ? 'today' : 
                                 diffDays === 1 ? 'tomorrow' : 
                                 `in ${diffDays} days`;
                        })()}
                      </Typography>
                    </Box>
                    </Box>
                  )}
              </Paper>
            );
          })}
        </Container>
      ) : (
        <Box>
          <TableContainer component={Paper}>
            <Table>
            <TableHead>
              <TableRow>
                  <TableCell>Class #</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Technique</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Next Class</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {classes.map((classItem) => {
                const progress = userProgress[classItem.id] || { 
                  completed_classes: 0,
                  dates: [] 
                };
                return (
                    <TableRow 
                      key={classItem.id}
                      ref={el => classRefs.current[`class-${classItem.number}`] = el}
                      sx={{
                        ...getCardStyle(classItem.number),
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        }
                      }}
                    >
                      <TableCell>
                        Class {classItem.number}
                      </TableCell>
                      <TableCell>
                        <ProgressButtonsContainer>
                          {[0, 1, 2, 3, 4].map((index) => (
                            <ProgressButton
                              key={index}
                              filled={progress.dates && progress.dates[index]}
                              date={progress.dates && progress.dates[index]}
                              onClick={() => handleProgressClick(
                                classItem.id,
                                index,
                                progress.dates && progress.dates[index]
                              )}
                              disabled={isButtonDisabled(progress.dates || [], index)}
                              loading={loadingActions[classItem.id]}
                              isExtra={index >= 3}
                            />
                          ))}
                        </ProgressButtonsContainer>
                      </TableCell>
                      <TableCell>
                      {classItem.techniques.map((technique, index) => (
                        <div key={technique.id}>
                            {technique.name_with_details}
                          {index < classItem.techniques.length - 1 && <br />}
                        </div>
                      ))}
                      </TableCell>
                      <TableCell>
                        <NotesButton 
                          classItem={classItem}
                          loading={loadingActions[`notes_${classItem.id}`]}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        {formatNextClassDisplay(getNextClass(classItem.number))}
                      </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            </Table>
        </TableContainer>
        </Box>
      )}

      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
      >
        <DialogTitle>
          {editingDate ? 'Edit Record Date' : 'Confirm Remove Record'}
        </DialogTitle>
        <DialogContent>
          {editingDate ? (
            <Box sx={{ pt: 2 }}>
              <TextField
                type="date"
                value={tempDate}
                onChange={handleDateChange}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          ) : (
            <DialogContentText>
              Are you sure you want to remove this class record?
              {selectedAction?.existingDate && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle2">
                    Date: {formatDateForDisplay(selectedAction.existingDate)}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleStartEdit(selectedAction.existingDate)}
                  >
                    Edit Date
                  </Button>
                </Box>
              )}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={editingDate ? handleCancelEdit : handleDialogClose}
            sx={{ 
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={editingDate ? handleConfirmEdit : handleRemoveRecord}
            variant="contained"
            color={editingDate ? "primary" : "error"}
            disabled={editingDate && !tempDate}
            sx={{ 
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem'
            }}
          >
            {editingDate ? 'Save' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <NotesDialog
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        classId={selectedClass}
      />
    </>
  );
};

export default TechniqueTracker;
